import React, { useState, useEffect } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import apiClient from './api/client';
import Select from 'react-select';
import { format } from 'date-fns';
import { Edit2, Eye, Search, RotateCcw, Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { FaToggleOn, FaToggleOff } from "react-icons/fa";
import CreateAdminUserModal from './components/CreateAdminUserModal';
import EditAdminUserModal from './components/EditAdminUserModal';
import ViewUserDomainsModal from './components/ViewUserDomainsModal';

const BRAND_GREEN = '#0aa375';

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '12px',
    borderColor: '#dee2e6',
    boxShadow: 'none',
    '&:hover': { borderColor: BRAND_GREEN },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
    color: state.isSelected ? 'white' : '#333',
  }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
};

const pageSizeOptions = [10, 25, 50, 100].map((n) => ({ value: n, label: `${n} / page` }));
const statusOptions = [
  { value: null, label: 'All Status' },
  { value: false, label: 'Enabled' },
  { value: true, label: 'Disabled' }
];

export default function ManageAdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [isDisabledFilter, setIsDisabledFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState({ value: false, label: 'Enabled' });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);

  const [showDomainsModal, setShowDomainsModal] = useState(false);
  const [selectedUserForDomains, setSelectedUserForDomains] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    loadRoles();
    loadAllUsers(1, pageSize);
  }, []);

  const loadRoles = async () => {
    try {
      const res = await apiClient.get('/api/userroles');
      const roleOptions = [
        { value: null, label: 'All Roles' },
        ...res.data.map(r => ({ value: r.id, label: r.text }))
      ];
      setRoles(roleOptions);
    } catch (err) {
      toast.error("Failed to load roles");
    }
  };

  const loadAllUsers = async (pageOverride, pageSizeOverride) => {
    const currentPage = pageOverride ?? page;
    const currentPageSize = pageSizeOverride ?? pageSize;
    setLoading(true);
    try {
      const res = await apiClient.get('/api/adminusers/list', {
        params: { page: currentPage, pageSize: currentPageSize, isDisabled: false }
      });
      setUsers(res.data.items || []);
      setTotalCount(res.data.totalCount ?? res.data.items?.length ?? 0);
      setPage(currentPage);
      setPageSize(currentPageSize);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (pageOverride, pageSizeOverride) => {
    const currentPage = pageOverride ?? 1;
    const currentPageSize = pageSizeOverride ?? pageSize;
    setLoading(true);
    try {
      const res = await apiClient.get('/api/adminusers/list', {
        params: {
          searchTerm: searchTerm.trim() || undefined,
          roleId: selectedRole?.value,
          isDisabled: statusFilter.value,
          page: currentPage,
          pageSize: currentPageSize,
        }
      });
      setUsers(res.data.items || []);
      setTotalCount(res.data.totalCount ?? res.data.items?.length ?? 0);
      setPage(currentPage);
      setPageSize(currentPageSize);
    } catch (err) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page || loading) return;
    handleSearch(newPage);
  };

  const handlePageSizeChange = (opt) => {
    handleSearch(1, opt.value);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedRole(null);
    setStatusFilter({ value: false, label: 'Enabled' });
    setPage(1);
    loadAllUsers(1, pageSize);
  };

  const refreshWithCurrentFilters = () => {
    if (searchTerm.trim() || selectedRole || statusFilter.value !== false) {
      handleSearch(page);
    } else {
      loadAllUsers(page, pageSize);
    }
  };
  const handleToggle = async (userId, currentDisabled) => {
    const currentDomain = sessionStorage.getItem("selectedDomain")
    try {
      await apiClient.post('/api/adminusers/toggle', {
        userId,
        disabled: !currentDisabled,
        domain: currentDomain
      });

      setUsers(prev => prev.map(u => 
        u.szUserId === userId ? { ...u, btIsDisabled: !currentDisabled } : u
      ));

      toast.success(!currentDisabled ? "User Disabled" : "User Enabled");
    } catch (err) {
      toast.error("Toggle failed");
    }
  };

  const openCreateModal = () => setShowCreateModal(true);

  const openEditModal = (user) => {
    setSelectedUserForEdit(user);
    setShowEditModal(true);
  };

  const openDomainsModal = (user) => {
    setSelectedUserForDomains(user);
    setShowDomainsModal(true);
  };

  return (
    <>
      <Toaster position="top-right" />

      <style>{`
        .tbl-compact td, .tbl-compact th { padding: 4px 8px !important; vertical-align: middle; }
        .tbl-compact thead th { background: #f8f9fa; fontSize: 11.5px; font-weight: 600; color: #495057; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid #dee2e6; white-space: nowrap; }
        .tbl-compact tbody tr { height: 36px; }
        .tbl-compact tbody tr:hover { background-color: #f0fdf8; }
        .form-control-xs { height: 30px !important; font-size: 12px !important; padding: 2px 8px !important; }
        .filter-label { font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px; display: block; }
        .domain-view-link { display: inline-flex; align-items: center; gap: 4px; border: none; background: transparent; color: ${BRAND_GREEN}; font-size: 12px; font-weight: 500; padding: 0; cursor: pointer; }
        .domain-view-link:hover { text-decoration: underline; }
        .filter-label { font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; display: block; }
      `}</style>

      <div className="bg-white" style={{ height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
        <div className="card shadow-sm border-0" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {showFilters && (
            <div className="card-body py-2 px-3" style={{ flex: '0 0 auto' }}>
              <div className="d-flex justify-content-between align-items-end gap-3 flex-wrap">
                
                <div className="d-flex gap-2 align-items-end flex-wrap">
                  
                  <div style={{ width: '260px' }}>
                    <span className="filter-label">Search Name</span>
                    <input
                      type="text"
                      className="form-control form-control-xs"
                      placeholder="Search Name..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                      autoComplete="off"
                    />
                  </div>

                  <div style={{ width: '200px' }}>
                    <span className="filter-label">Role</span>
                    <Select
                      options={roles}
                      value={selectedRole}
                      onChange={setSelectedRole}
                      placeholder="Select Role..."
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div style={{ width: '160px' }}>
                    <span className="filter-label">Status</span>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(o => o.value === statusFilter.value)}
                      onChange={setStatusFilter}
                      placeholder="Select Status..."
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <button
                    className="btn btn-sm text-white d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: BRAND_GREEN,
                      height: '28px',
                      width: window.innerWidth < 768 ? '36px' : 'auto',
                      padding: window.innerWidth < 768 ? '0' : '0 12px'
                    }}
                    onClick={() => handleSearch(1)}
                    disabled={loading}
                    title="Search"
                  >
                    {window.innerWidth < 768 ? <Search size={15} /> : 'Search'}
                  </button>

                  <button
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
                    style={{
                      height: '28px',
                      width: window.innerWidth < 768 ? '36px' : 'auto',
                      padding: window.innerWidth < 768 ? '0' : '0 12px'
                    }}
                    onClick={handleReset}
                    disabled={loading}
                    title="Reset"
                  >
                    {window.innerWidth < 768 ? <RotateCcw size={15} /> : 'Reset'}
                  </button>

                  <button
                    className="btn btn-sm text-white d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: BRAND_GREEN,
                      height: '28px',
                      width: window.innerWidth < 768 ? '36px' : 'auto',
                      padding: window.innerWidth < 768 ? '0' : '0 12px'
                    }}
                    onClick={openCreateModal}
                    title="Create New User"
                  >
                    {window.innerWidth < 768 ? <Plus size={16} /> : 'Create New User'}
                  </button>

                </div>

                <button
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 d-md-none ms-auto"
                  style={{ 
                    height: '28px', 
                    fontSize: '11px', 
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                  onClick={() => setShowFilters(false)}
                  title="Hide Filters"
                >
                  Hide Filters <ChevronUp size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
            {!showFilters && (
              <button
                className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1"
                style={{ fontSize: '12px', height: '28px' }}
                onClick={() => setShowFilters(true)}
              >
                Show Filters <ChevronDown size={14} />
              </button>
            )}
          </div>

          <div className="table-responsive" style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
            
            <table className="table table-hover mb-0 tbl-compact d-none d-md-table" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky-top">
                <tr>
                  <th style={{ width: '22%' }}>User Name</th>
                  <th style={{ width: '15%' }}>Role</th>
                  <th style={{ width: '22%' }}>Email</th>
                  <th style={{ width: '13%' }}>Created On</th>
                  <th style={{ width: '13%' }}>Active Domain</th>
                  <th style={{ width: '140px' }} className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-4"><span className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} /></td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>No users found</td></tr>
                ) : (
                  users.map(user => (
                    <tr key={user.szUserId}>
                      <td style={{ fontSize: '12px' }}>
                        <button
                          className="btn btn-link p-0 text-start"
                          style={{ fontSize: '12px', color: BRAND_GREEN, textDecoration: 'none', fontWeight: 500 }}
                          onClick={() => openEditModal(user)}
                        >
                          {user.szUserName}
                        </button>
                      </td>
                      <td style={{ fontSize: '12px' }}>{user.szUserRole || user.userRoleName}</td>
                      <td style={{ fontSize: '12px' }}>{user.szEmail}</td>
                      <td style={{ fontSize: '12px' }}>
                        {user.dtCreated ? format(new Date(user.dtCreated), 'dd-MMM-yy') : '—'}
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        <button
                          className="domain-view-link"
                          onClick={() => openDomainsModal(user)}
                          title="View active domains"
                        >
                          {/* <Eye size={13} /> */}
                          Click to view
                        </button>
                      </td>
                      <td className="text-center">
                        <button 
                          className="btn btn-sm me-2 p-1" 
                          style={{ border: 'none', background: 'transparent', color: BRAND_GREEN }} 
                          onClick={() => openEditModal(user)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="btn btn-sm text-white px-3 py-1" 
                          style={{ 
                            backgroundColor: user.btIsDisabled ? '#0aa375' : '#dc3545', 
                            fontSize: '11px',
                            minWidth: '75px'
                          }} 
                          onClick={() => handleToggle(user.szUserId, user.btIsDisabled)}
                        >
                          {user.btIsDisabled ? 'Enable' : 'Disable'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/*  Card View */}
            <div className="d-md-none" style={{ padding: '0 12px' }}>
              {loading ? (
                <div className="text-center py-4"><span className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} /></div>
              ) : users.length === 0 ? (
                <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>No users found</div>
              ) : (
                users.map(user => (
                  <div key={user.szUserId} style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '14px',
                    marginBottom: '8px', 
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontSize: '14.5px' }}>
                          <strong>USER NAME:</strong>{' '}
                          <button className="btn btn-link p-0 text-start"
                            style={{ fontSize: '14.5px', color: BRAND_GREEN, textDecoration: 'none', fontWeight: 500 }}
                            onClick={() => openEditModal(user)}
                          >
                            {user.szUserName}
                          </button>
                        </div>    
                        <div style={{ fontSize: '14px' }}><strong>ROLE:</strong> {user.szUserRole || user.userRoleName}</div>
                      </div>
                      <button 
                        className="btn btn-sm d-flex align-items-center justify-content-center"
                        style={{ 
                          backgroundColor: user.btIsDisabled ? '#dc3545' : '#0aa375', 
                          color: 'white',
                          fontSize: '13px',
                          width: '32px',
                          height: '26px',
                          padding: '0',
                          border: 'none'
                        }} 
                        onClick={() => handleToggle(user.szUserId, user.btIsDisabled)}
                      >
                        {user.btIsDisabled ? <FaToggleOff size={16} /> : <FaToggleOn size={16} />}
                      </button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '14px' }}>
                        <strong>EMAIL:</strong> {user.szEmail}
                      </div>
                      <button 
                        className="btn btn-sm" 
                        style={{ color: BRAND_GREEN }}
                        onClick={() => openEditModal(user)}
                      >
                        <Edit2 size={15} /> 
                      </button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '14px' }}>
                        <strong>CREATED:</strong> {user.dtCreated ? format(new Date(user.dtCreated), 'dd-MMM-yy') : '—'}
                      </div>
                      <button
                        className="domain-view-link"
                        onClick={() => openDomainsModal(user)}
                        style={{ fontSize: '13px', padding: '6px 12px' }}
                        title="View Domains"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {!loading && users.length > 0 && (
            <div style={{
              flex: '0 0 auto',
              padding: window.innerWidth < 768 ? '2px 10px 6px 10px' : '4px 14px 8px 14px',
              borderTop: '1px solid #e5e7eb',display: 'flex', 
              justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', 
              flexWrap: window.innerWidth < 768 ? 'nowrap' : 'wrap',
              gap: window.innerWidth < 768 ? '6px' : '10px',
            }}>            
              <span style={{ fontSize: window.innerWidth < 768 ? '11px' : '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                {window.innerWidth < 768 ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} / ${totalCount}` : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
              </span>

              <div style={{ display: 'flex', gap: window.innerWidth < 768 ? '2px' : '4px', alignItems: 'center' }}>
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1 || loading}
                  style={{
                    padding: window.innerWidth < 768 ? '4px 8px' : '6px 10px', 
                    borderRadius: 6, border: '1px solid #d1fae5',
                    background: page === 1 ? '#f3f4f6' : '#fff',
                    color: page === 1 ? '#9ca3af' : BRAND_GREEN,
                    cursor: page === 1 ? 'not-allowed' : 'pointer', 
                    fontSize: window.innerWidth < 768 ? '11px' : '12px'
                  }}
                >{window.innerWidth < 768 ? '‹' : '‹ Prev'}</button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => {
                    if (totalPages <= 7) return true;
                    if (p === 1 || p === totalPages) return true;
                    if (p >= page - 1 && p <= page + 1) return true;
                    return false;
                  })
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{color: '#9ca3af', fontSize: window.innerWidth < 768 ? '11px' : '12px', padding: '0 4px' }}>...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p)}
                        disabled={loading}
                        style={{
                          width: window.innerWidth < 768 ? '26px' : '30px', 
                          height: window.innerWidth < 768 ? '26px' : '30px', 
                          borderRadius: 6, border: '1px solid #d1fae5',
                          background: page === p ? BRAND_GREEN : '#fff',
                          color: page === p ? '#fff' : BRAND_GREEN,
                          fontWeight: page === p ? 700 : 400, cursor: 'pointer', 
                          fontSize: window.innerWidth < 768 ? '11px' : '12px'
                        }}
                      >{p}</button>
                    )
                  )
                }

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages || loading}
                  style={{
                    padding: window.innerWidth < 768 ? '4px 8px' : '6px 10px', 
                    borderRadius: 6, border: '1px solid #d1fae5',
                    background: page === totalPages ? '#f3f4f6' : '#fff',
                    color: page === totalPages ? '#9ca3af' : BRAND_GREEN,
                    cursor: page === totalPages ? 'not-allowed' : 'pointer', 
                    fontSize: window.innerWidth < 768 ? '11px' : '12px'
                  }}
                >{window.innerWidth < 768 ? '›' : 'Next ›'}</button>
              </div>

             <div style={{ width: window.innerWidth < 768 ? '85px' : '120px', minWidth: window.innerWidth < 768 ? '85px' : '120px' }}>
                <Select
                  options={pageSizeOptions}
                  value={pageSizeOptions.find(o => o.value === pageSize)}
                  onChange={handlePageSizeChange}
                  isSearchable={false}
                  isDisabled={loading}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateAdminUserModal 
        show={showCreateModal} 
        onClose={() => setShowCreateModal(false)} 
        roles={roles} 
        onSuccess={refreshWithCurrentFilters}
      />

      <EditAdminUserModal 
        show={showEditModal} 
        onClose={() => setShowEditModal(false)} 
        user={selectedUserForEdit} 
        roles={roles} 
        onSuccess={refreshWithCurrentFilters}
      />

      <ViewUserDomainsModal
        show={showDomainsModal}
        onClose={() => setShowDomainsModal(false)}
        user={selectedUserForDomains}
      />
    </>
  );
}